/**
 * Sawyer E4 (#754) — the staff board: one list every staffer at the
 * facility reads. "Hey, I noticed Bush is limping today, keep an eye on it."
 *
 * Composer on top (Post is the one primary button, P2), newest post first.
 * Posting is optimistic (P4); a failed post stays in the list marked "Not
 * sent" with Retry and the text goes back into the composer (AC20). Posts
 * newer than the marker the server returned on open, and not mine, carry
 * "New" (AC13); after the first page renders the marker moves up to the
 * newest post SHOWN, never past it (review C11).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  personName,
  roleLabel,
  staffMessagesApi,
  type DogTag,
  type Post,
} from '../../lib/staff-messages-api.js';
import { DogTagPicker } from './DogTagPicker.js';
import { refreshUnread } from './useStaffMessagesUnread.js';
import { bodyState, Composer, LoadError, SkeletonRows, StaleLine, TimeStamp, useVisiblePoll } from './shared.js';

const BOARD_REFRESH_MS = 30_000;

type Pending = { tempId: string; body: string; dog: DogTag | null; status: 'sending' | 'failed' };

type Load =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; posts: Post[]; nextCursor: string | null };

export interface BoardPanelProps {
  apiUrl: string;
  canWrite: boolean;
  canOpenDogs: boolean;
  /** The signed-in staffer, for the optimistic post header (AC1). */
  me: { name: string; role: string };
}

/** "New": newer than the marker the server returned on open, and not mine. */
export function isNew(post: Pick<Post, 'createdAt' | 'isMine'>, lastReadAt: string | null): boolean {
  if (post.isMine) return false;
  if (lastReadAt === null) return true;
  return Date.parse(post.createdAt) > Date.parse(lastReadAt);
}

export function DogChip({ dog, canOpen }: { dog: DogTag; canOpen: boolean }): JSX.Element {
  return canOpen ? (
    <Link className="ferro-tm-dog" to={`/dogs/${encodeURIComponent(dog.id)}`} data-testid="board-dog-chip">
      {dog.name}
    </Link>
  ) : (
    <span className="ferro-tm-dog" data-testid="board-dog-chip">
      {dog.name}
    </span>
  );
}

export function BoardPanel({ apiUrl, canWrite, canOpenDogs, me }: BoardPanelProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  // The marker as it stood when this board was opened (drives "New").
  const [openedMarker, setOpenedMarker] = useState<string | null | undefined>(undefined);
  const [pending, setPending] = useState<Pending[]>([]);
  const [draft, setDraft] = useState('');
  const [dog, setDog] = useState<DogTag | null>(null);
  const [picking, setPicking] = useState(false);
  const [stale, setStale] = useState(false);
  const [lastGood, setLastGood] = useState<Date | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const markedUpTo = useRef<string | null>(null);

  const markRead = useCallback(
    (posts: Post[]) => {
      const newest = posts[0]?.createdAt;
      if (!newest || newest === markedUpTo.current) return;
      markedUpTo.current = newest;
      void staffMessagesApi.markBoardRead(apiUrl, newest).then(() => refreshUnread());
    },
    [apiUrl],
  );

  const loadFirst = useCallback(async () => {
    setLoad({ kind: 'loading' });
    const r = await staffMessagesApi.board(apiUrl);
    if (!r.ok) {
      setLoad({ kind: 'error' });
      return;
    }
    setOpenedMarker((m) => (m === undefined ? r.data.lastReadAt : m));
    setLoad({ kind: 'ready', posts: r.data.posts, nextCursor: r.data.nextCursor });
    setLastGood(new Date());
    setStale(false);
    markRead(r.data.posts);
  }, [apiUrl, markRead]);

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  // Refresh the newest page while open; a failure keeps the list (MAC6).
  const refresh = useCallback(async () => {
    if (load.kind !== 'ready') return;
    const r = await staffMessagesApi.board(apiUrl);
    if (!r.ok) {
      setStale(true);
      return;
    }
    setStale(false);
    setLastGood(new Date());
    setLoad((cur) => {
      if (cur.kind !== 'ready') return cur;
      const fresh = new Set(r.data.posts.map((p) => p.id));
      const older = cur.posts.filter((p) => !fresh.has(p.id));
      return { ...cur, posts: [...r.data.posts, ...older] };
    });
    markRead(r.data.posts);
  }, [apiUrl, load.kind, markRead]);
  useVisiblePoll(() => void refresh(), BOARD_REFRESH_MS, load.kind === 'ready');

  async function loadOlder(): Promise<void> {
    if (load.kind !== 'ready' || !load.nextCursor) return;
    setLoadingOlder(true);
    const r = await staffMessagesApi.board(apiUrl, load.nextCursor);
    setLoadingOlder(false);
    if (!r.ok) {
      setStale(true);
      return;
    }
    setLoad((cur) =>
      cur.kind === 'ready'
        ? {
            kind: 'ready',
            posts: [...cur.posts, ...r.data.posts.filter((p) => !cur.posts.some((c) => c.id === p.id))],
            nextCursor: r.data.nextCursor,
          }
        : cur,
    );
  }

  async function send(body: string, tag: DogTag | null, tempId: string): Promise<void> {
    const r = await staffMessagesApi.postBoard(apiUrl, body, tag?.id ?? null);
    if (r.ok) {
      setPending((list) => list.filter((p) => p.tempId !== tempId));
      setLoad((cur) => (cur.kind === 'ready' ? { ...cur, posts: [r.data.post, ...cur.posts] } : cur));
      return;
    }
    setPending((list) => list.map((p) => (p.tempId === tempId ? { ...p, status: 'failed' } : p)));
    // Keep the words: back into the composer when it is empty (AC20).
    setDraft((d) => (d.trim() === '' ? body : d));
    if (tag) setDog((d) => d ?? tag);
  }

  function submit(): void {
    const { trimmed, empty, over } = bodyState(draft);
    if (empty || over) return;
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPending((list) => [{ tempId, body: trimmed, dog, status: 'sending' }, ...list]);
    const tag = dog;
    setDraft('');
    setDog(null);
    void send(trimmed, tag, tempId);
  }

  function retry(p: Pending): void {
    setPending((list) => list.map((x) => (x.tempId === p.tempId ? { ...x, status: 'sending' } : x)));
    if (draft.trim() === p.body) {
      setDraft('');
      setDog(null);
    }
    void send(p.body, p.dog, p.tempId);
  }

  const posts = load.kind === 'ready' ? load.posts : [];
  const empty = load.kind === 'ready' && posts.length === 0 && pending.length === 0;

  return (
    <div className="ferro-tm-board" data-testid="team-messages-board">
      {canWrite ? (
        <div className="ferro-tm-card ferro-tm-board__composer">
          <Composer
            testId="board-composer"
            label="Message to the whole team"
            placeholder="Tell the whole team something, like a dog to keep an eye on today."
            value={draft}
            onChange={setDraft}
            onSubmit={submit}
            submitLabel="Post"
            focusOnMount={empty}
            extra={
              dog ? (
                <span className="ferro-tm-dog ferro-tm-dog--selected" data-testid="board-selected-dog">
                  {dog.name}
                  <button
                    type="button"
                    className="ferro-tm-dog__remove"
                    aria-label={`Remove ${dog.name}`}
                    onClick={() => setDog(null)}
                  >
                    ×
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline ferro-tm-board__tag"
                  onClick={() => setPicking(true)}
                  data-testid="board-tag-dog"
                >
                  Tag a dog
                </button>
              )
            }
          />
        </div>
      ) : (
        <p className="ferro-tm-readonly" data-testid="board-readonly">
          You can read the team board. Posting isn&apos;t part of your role.
        </p>
      )}

      {picking ? (
        <DogTagPicker
          apiUrl={apiUrl}
          onClose={() => setPicking(false)}
          onPick={(d) => {
            setDog(d);
            setPicking(false);
          }}
        />
      ) : null}

      {stale ? <StaleLine lastGood={lastGood} /> : null}

      {load.kind === 'loading' ? <SkeletonRows count={3} variant="post" /> : null}
      {load.kind === 'error' ? <LoadError testId="board-error" onRetry={() => void loadFirst()} /> : null}

      {empty ? (
        <div className="ferro-tm-empty" data-testid="board-empty">
          <p>Nothing on the board yet. Post something the whole team should know, like a dog to keep an eye on today.</p>
        </div>
      ) : null}

      {load.kind === 'ready' && (posts.length > 0 || pending.length > 0) ? (
        <ol className="ferro-tm-posts" data-testid="board-posts">
          {pending.map((p) => (
            <li key={p.tempId} className="ferro-tm-card ferro-tm-post ferro-tm-post--pending" data-testid="board-post-pending">
              <div className="ferro-tm-post__head">
                <span className="ferro-tm-post__author">{me.name || 'You'}</span>
                {roleLabel(me.role) ? <span className="ferro-tm__muted">{roleLabel(me.role)}</span> : null}
                {p.status === 'sending' ? (
                  <span className="ferro-tm__time">just now</span>
                ) : (
                  <span className="ferro-tm-post__failed" data-testid="board-post-failed">
                    Not sent
                    <button type="button" className="ferro-tm-link" onClick={() => retry(p)}>
                      Retry
                    </button>
                  </span>
                )}
              </div>
              <p className="ferro-tm-post__body">{p.body}</p>
              {p.dog ? <DogChip dog={p.dog} canOpen={false} /> : null}
            </li>
          ))}
          {posts.map((post) => (
            <li key={post.id} className="ferro-tm-card ferro-tm-post" data-testid="board-post">
              <div className="ferro-tm-post__head">
                <span className="ferro-tm-post__author">{personName(post.author)}</span>
                {roleLabel(post.author.role) ? (
                  <span className="ferro-tm__muted">{roleLabel(post.author.role)}</span>
                ) : null}
                <TimeStamp iso={post.createdAt} />
                {openedMarker !== undefined && isNew(post, openedMarker) ? (
                  <span className="ferro-tm-new" data-testid="board-post-new">
                    New
                  </span>
                ) : null}
              </div>
              <p className="ferro-tm-post__body">{post.body}</p>
              {post.dog ? <DogChip dog={post.dog} canOpen={canOpenDogs} /> : null}
            </li>
          ))}
        </ol>
      ) : null}

      {load.kind === 'ready' && load.nextCursor ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--outline ferro-tm-older"
          onClick={() => void loadOlder()}
          disabled={loadingOlder}
          data-testid="board-load-older"
        >
          {loadingOlder ? 'Loading…' : 'Load older'}
        </button>
      ) : null}
    </div>
  );
}
